import React, { useMemo } from "react";
import { TransformControls } from "@react-three/drei";
import {
  ThreeEvent,
  createPortal,
  useFrame,
  useThree,
} from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { TransformControls as TransformControlsImpl } from "three-stdlib";
import { editorStoreContext } from "../../../../../store/editorStore";
import { FloorplannerStoreContext } from "../../../../../store/floorplannerStore";
import { SymbolType, WallType } from "../../../../../types/floorplannerTypes";
import {
  useSelectedSymbol3DDrag,
  type Symbol3DDragVisualState,
} from "../../../interaction/useSelectedSymbol3DDrag";
import { getSymbol3DGroupTranslateOffset } from "../../../interaction/symbol3DGroupTranslate";
import {
  applyRotationGizmoSensitivity,
  createRotationGizmoSensitivitySession,
  getRotationGizmoControlSize,
  getSymbol3DRotationFromQuaternion,
  isPointerOverTransformControlsPicker,
  registerTransformControlsPointerGuard,
  ROTATION_GIZMO_SNAP_STEP,
  syncSymbolPlanRotationAfter3DYawChange,
  type RotationGizmoSensitivitySession,
  type Symbol3DRotation,
} from "../../../interaction/symbol3DRotationGizmo";
import {
  getBuiltInFurnitureMaterial,
  isGlassBuiltInFurnitureMaterial,
} from "../../../materials/builtInFurnitureMaterials";
import CatalogStandardMaterial from "../../../materials/CatalogStandardMaterial";
import LitPatternMaterial from "../../../materials/LitPatternMaterial";
import { resolveMaterial } from "../../../materials/materialResolver";
import {
  hasAssignedSymbolMaterial,
  resolvePrimitiveSymbolFillColor,
  resolveSymbolPattern,
} from "../../../materials/primitiveSymbolFill";
import {
  overlayBuiltInMaterialSurfaceRef,
  useBuiltInMaterialSurfaceMappingsRevision,
} from "../../../materials/builtInMaterialSurfaceMappings";
import { getPrimitiveSymbol3DModelKeyForSymbol } from "../../registry/primitiveSymbol3DConfig";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import {
  createShapePrimitiveGeometryDefinition,
  createShapePrimitiveWorldMatrix,
  isShapePrimitiveSymbol,
} from "./shapePrimitiveGeometry";
import { resolveFillOpacity } from "../../../../../utils/fillOpacity";

type ShapePrimitive3DMeshesProps = {
  symbols: SymbolType[];
  walls: WallType[];
  selectedSymbolIds: Set<string>;
  claimSelectionEvents?: boolean;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onDragActiveChange?: (active: boolean) => void;
};

const ShapePrimitive3DMeshes: React.FC<ShapePrimitive3DMeshesProps> = observer(
  ({
    symbols,
    walls,
    selectedSymbolIds,
    claimSelectionEvents = false,
    onSelectSymbol,
    onDragActiveChange,
  }) => (
    <>
      {symbols.filter(shouldRenderAsShapePrimitive).map((symbol) => (
        <ShapePrimitive3DMesh
          key={symbol.id}
          symbol={symbol}
          walls={walls}
          selected={selectedSymbolIds.has(symbol.id)}
          claimSelectionEvents={claimSelectionEvents}
          onSelectSymbol={onSelectSymbol}
          onDragActiveChange={onDragActiveChange}
        />
      ))}
    </>
  ),
);

function shouldRenderAsShapePrimitive(symbol: SymbolType): boolean {
  return (
    isShapePrimitiveSymbol(symbol) &&
    getPrimitiveSymbol3DModelKeyForSymbol(symbol) === undefined
  );
}

const ShapePrimitive3DMesh: React.FC<{
  symbol: SymbolType;
  walls: WallType[];
  selected: boolean;
  claimSelectionEvents?: boolean;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onDragActiveChange?: (active: boolean) => void;
}> = observer(
  ({
    symbol,
    walls,
    selected,
    claimSelectionEvents = false,
    onSelectSymbol,
    onDragActiveChange,
  }) => {
    const editorStore = React.useContext(editorStoreContext);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { camera, invalidate, scene } = useThree();
    const groupRef = React.useRef<THREE.Group | null>(null);
    const rotationWorldPositionRef = React.useRef(new THREE.Vector3());
    const transformControlsRef = React.useRef<TransformControlsImpl | null>(
      null,
    );
    const rotateTransactionRef = React.useRef(false);
    const rotateChangedRef = React.useRef(false);
    const rotationStart3DRef = React.useRef<Symbol3DRotation | undefined>(
      undefined,
    );
    const rotationSensitivityRef =
      React.useRef<RotationGizmoSensitivitySession | null>(null);
    const [isRotationDragging, setIsRotationDragging] = React.useState(false);
    const [rotationObject, setRotationObject] =
      React.useState<THREE.Group | null>(null);
    const definition = useMemo(
      () => createShapePrimitiveGeometryDefinition(symbol),
      [
        symbol,
        symbol.type,
        symbol.threeD?.shapeKind,
        symbol.threeD?.height,
        symbol.threeD?.baseElevation,
        "squareWidth" in symbol ? symbol.squareWidth : undefined,
        "squareHeight" in symbol ? symbol.squareHeight : undefined,
        "circleWidth" in symbol ? symbol.circleWidth : undefined,
        "circleHeight" in symbol ? symbol.circleHeight : undefined,
        "triangleWidth" in symbol ? symbol.triangleWidth : undefined,
        "triangleHeight" in symbol ? symbol.triangleHeight : undefined,
        "polygonKind" in symbol ? symbol.polygonKind : undefined,
        "polygonWidth" in symbol ? symbol.polygonWidth : undefined,
        "polygonHeight" in symbol ? symbol.polygonHeight : undefined,
      ],
    );
    const matrix = useMemo(
      () => createShapePrimitiveWorldMatrix(symbol, walls),
      [
        symbol,
        walls,
        symbol.position.x,
        symbol.position.y,
        symbol.rotation,
        symbol.threeD?.placementMode,
        symbol.threeD?.baseElevation,
        symbol.threeD?.rotation3D?.pitch,
        symbol.threeD?.rotation3D?.roll,
        symbol.threeD?.rotation3D?.yaw,
        symbol.threeD?.wallMount?.wallId,
        symbol.threeD?.wallMount?.side,
        symbol.threeD?.wallMount?.offset,
        symbol.threeD?.wallMount?.distanceFromWall,
      ],
    );
    const matrixTransform = useMemo(() => decomposeMatrix(matrix), [matrix]);
    const shape = useMemo(() => {
      if (!definition || definition.points.length < 3) return null;
      const nextShape = new THREE.Shape();
      nextShape.moveTo(definition.points[0][0], definition.points[0][1]);
      definition.points.slice(1).forEach(([x, y]) => nextShape.lineTo(x, y));
      nextShape.closePath();
      return nextShape;
    }, [definition]);
    const extrudeSettings = useMemo(() => {
      if (!definition) return null;
      return {
        depth: definition.thickness,
        bevelEnabled: true,
        bevelSize: Math.min(definition.thickness * 0.08, 0.01),
        bevelThickness: Math.min(definition.thickness * 0.08, 0.01),
        bevelSegments: 2,
        curveSegments: 16,
      };
    }, [definition]);
    const selectionHandlers = usePointerUpSelection(symbol, onSelectSymbol, {
      claimPointerEvents: claimSelectionEvents,
    });
    const applyVisualState = React.useCallback(
      (object: THREE.Object3D, state: Symbol3DDragVisualState) => {
        applyMatrixTransform(
          object,
          createShapePrimitiveWorldMatrix(
            {
              ...symbol,
              position: state.position,
              threeD: state.threeD ?? symbol.threeD,
            },
            walls,
          ),
        );
        object.updateMatrixWorld(true);
      },
      [symbol, walls],
    );
    const dragHandlers = useSelectedSymbol3DDrag({
      symbol,
      selected,
      walls,
      onDragActiveChange,
      visualTargetRef: groupRef,
      applyVisualState,
    });
    const materialColor = resolvePrimitiveSymbolFillColor(symbol);
    const fillOpacity = resolveFillOpacity(symbol);
    const fillIsTransparent = fillOpacity < 1;
    const fillPattern = resolveSymbolPattern(symbol);
    const mappingsRevision = useBuiltInMaterialSurfaceMappingsRevision();
    const assignedMaterial = React.useMemo(
      () =>
        Object.values(symbol.threeD?.materialAssignments ?? {}).find(
          (assignment) =>
            Boolean(assignment?.materialId || assignment?.surfaceRef?.id),
        ),
      [symbol.threeD?.materialAssignments],
    );
    const assignedSurfaceRef = React.useMemo(
      () =>
        overlayBuiltInMaterialSurfaceRef(
          symbol.threeD?.surfaceRef ?? assignedMaterial?.surfaceRef,
          assignedMaterial?.materialId ??
            symbol.threeD?.surfaceRef?.id ??
            assignedMaterial?.surfaceRef?.id,
        ),
      [
        assignedMaterial?.materialId,
        assignedMaterial?.surfaceRef,
        mappingsRevision,
        symbol.threeD?.surfaceRef,
      ],
    );
    const assignedBuiltIn = React.useMemo(
      () =>
        getBuiltInFurnitureMaterial(
          assignedMaterial?.materialId ?? assignedSurfaceRef?.id,
        ),
      [assignedMaterial?.materialId, assignedSurfaceRef?.id],
    );
    const assignedMaterialResolution = React.useMemo(
      () =>
        assignedMaterial || assignedSurfaceRef
          ? resolveMaterial("wall", symbol, {
              defaultColor: materialColor,
              materialId: assignedMaterial?.materialId ?? assignedSurfaceRef?.id,
            })
          : null,
      [assignedMaterial, assignedSurfaceRef, materialColor, symbol],
    );
    const assignedMaterialColor =
      assignedMaterialResolution?.color ?? materialColor;
    const assignedIsGlass = isGlassBuiltInFurnitureMaterial(assignedBuiltIn);
    const shapePresetDefaults = assignedBuiltIn
      ? {
          roughness: assignedBuiltIn.roughness,
          metalness: assignedBuiltIn.metalness,
        }
      : undefined;
    const rotationControlsVisible = Boolean(
      !editorStore.panning &&
        selected &&
        rotationObject &&
        definition &&
        editorStore.selections.length === 1 &&
        editorStore.selections[0]?.id === symbol.id,
    );
    const rotationControlDimensions = useMemo(
      () =>
        definition
          ? {
              width: definition.dimensions.width,
              depth: definition.dimensions.depth,
              height: definition.thickness,
            }
          : {},
      [definition],
    );
    const rotationControlSize = getRotationGizmoControlSize(
      rotationControlDimensions,
    );
    const setGroupRef = React.useCallback((group: THREE.Group | null) => {
      groupRef.current = group;
      setRotationObject(group);
    }, []);
    React.useLayoutEffect(() => {
      if (!rotationControlsVisible || !transformControlsRef.current) return;
      return registerTransformControlsPointerGuard(
        transformControlsRef.current,
      );
    }, [rotationControlsVisible]);
    const beginRotation = React.useCallback(() => {
      if (!rotationObject || rotateTransactionRef.current) return;
      rotateTransactionRef.current = true;
      rotateChangedRef.current = false;
      setIsRotationDragging(true);
      rotationStart3DRef.current = floorplannerStore.symbolsMap.get(
        symbol.id,
      )?.threeD?.rotation3D;
      rotationSensitivityRef.current =
        createRotationGizmoSensitivitySession(rotationObject);
      floorplannerStore.beginUndoTransaction("rotate 3D shape");
      floorplannerStore.recordSymbolForUndo(symbol.id);
      onDragActiveChange?.(true);
    }, [floorplannerStore, onDragActiveChange, rotationObject, symbol.id]);
    const updateRotation = React.useCallback(() => {
      if (!rotationObject) return;
      applyRotationGizmoSensitivity(rotationObject, rotationSensitivityRef.current, {
        snapStep: editorStore.isShiftPressed ? ROTATION_GIZMO_SNAP_STEP : null,
      });
      rotateChangedRef.current = true;
      invalidate();
    }, [editorStore.isShiftPressed, invalidate, rotationObject]);
    const finishRotation = React.useCallback(() => {
      if (!rotationObject || !rotateTransactionRef.current) return;
      rotateTransactionRef.current = false;
      setIsRotationDragging(false);
      rotationSensitivityRef.current = null;
      onDragActiveChange?.(false);

      if (!rotateChangedRef.current) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const currentSymbol = floorplannerStore.symbolsMap.get(symbol.id);
      if (!currentSymbol) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const nextRotation3D = getShapePrimitiveLocalRotationFromObject(
        rotationObject,
        currentSymbol,
        walls,
      );
      const { rotation, rotation3D } = syncSymbolPlanRotationAfter3DYawChange(
        currentSymbol.rotation,
        rotationStart3DRef.current,
        nextRotation3D,
      );
      floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
        ...currentSymbol.threeD,
        rotation3D,
      });
      floorplannerStore.updateSymbolProperty(symbol.id, "rotation", rotation);
      floorplannerStore.recordSymbolChange(symbol.id, "modify");
      floorplannerStore.pushToUndoStack();
      invalidate();
    }, [
      floorplannerStore,
      invalidate,
      onDragActiveChange,
      rotationObject,
      symbol.id,
      walls,
    ]);
    React.useEffect(
      () => () => {
        if (rotateTransactionRef.current) {
          rotateTransactionRef.current = false;
          setIsRotationDragging(false);
          floorplannerStore.cancelUndoTransaction();
          onDragActiveChange?.(false);
        }
      },
      [floorplannerStore, onDragActiveChange],
    );
    const updateRotationControlSize = React.useCallback(
      (currentCamera: THREE.Camera) => {
        if (
          !rotationControlsVisible ||
          !rotationObject ||
          !transformControlsRef.current
        ) {
          return;
        }
        rotationObject.updateMatrixWorld(true);
        transformControlsRef.current.setSize(
          getRotationGizmoControlSize(rotationControlDimensions, {
            camera: currentCamera,
            worldPosition: rotationObject.getWorldPosition(
              rotationWorldPositionRef.current,
            ),
          }),
        );
      },
      [rotationControlDimensions, rotationControlsVisible, rotationObject],
    );
    React.useLayoutEffect(() => {
      updateRotationControlSize(camera);
    }, [camera, updateRotationControlSize]);
    useFrame(({ camera }) => {
      updateRotationControlSize(camera);
    });

    if (!definition || !shape || !extrudeSettings) return null;

    const pointerHandlers = {
      onPointerDown: (event: ThreeEvent<PointerEvent>) => {
        if (
          isPointerOverTransformControlsPicker(
            transformControlsRef.current,
            event,
          )
        ) {
          return;
        }
        dragHandlers.onPointerDown(event);
        selectionHandlers.onPointerDown(event);
      },
      onPointerMove: (event: ThreeEvent<PointerEvent>) => {
        dragHandlers.onPointerMove(event);
        selectionHandlers.onPointerMove(event);
      },
      onPointerUp: (event: ThreeEvent<PointerEvent>) => {
        dragHandlers.onPointerUp(event);
        selectionHandlers.onPointerUp(event);
      },
      onPointerCancel: (event: ThreeEvent<PointerEvent>) => {
        dragHandlers.onPointerCancel(event);
      },
    };

    const groupTranslateOffset = getSymbol3DGroupTranslateOffset(symbol.id);

    return (
      <>
        <group
          ref={setGroupRef}
          position={[
            matrixTransform.position.x + groupTranslateOffset.x,
            matrixTransform.position.y + groupTranslateOffset.y,
            matrixTransform.position.z,
          ]}
          {...(!isRotationDragging && {
            quaternion: matrixTransform.quaternion,
          })}
          scale={matrixTransform.scale}
        >
          <mesh
            castShadow
            receiveShadow
            {...pointerHandlers}
            userData={{
              floorplannerType: "symbol",
              floorplannerId: symbol.id,
              floorplannerSymbolKind: symbol.type,
              floorplannerPrimitivePart: "shape",
            }}
          >
            <extrudeGeometry args={[shape, extrudeSettings]} />
            {fillPattern ? (
              <LitPatternMaterial
                fillColor={materialColor}
                fillPattern={fillPattern}
                roughness={0.78}
                side={THREE.DoubleSide}
                opacity={fillOpacity}
                transparent={fillIsTransparent}
                depthWrite={!fillIsTransparent}
              />
            ) : hasAssignedSymbolMaterial(symbol) &&
              assignedIsGlass &&
              assignedBuiltIn ? (
              <meshPhysicalMaterial
                color={assignedMaterialColor}
                roughness={
                  assignedSurfaceRef?.roughness ?? assignedBuiltIn.roughness
                }
                metalness={0}
                transmission={assignedBuiltIn.transmission ?? 1}
                thickness={assignedBuiltIn.thickness ?? 0.05}
                ior={assignedBuiltIn.ior ?? 1.5}
                transparent
                opacity={fillOpacity < 1 ? fillOpacity : 1}
                side={THREE.DoubleSide}
                depthWrite={!fillIsTransparent}
              />
            ) : hasAssignedSymbolMaterial(symbol) ? (
              <CatalogStandardMaterial
                color={assignedMaterialColor}
                surfaceRef={assignedSurfaceRef}
                defaults={shapePresetDefaults}
                side={THREE.DoubleSide}
                opacity={fillOpacity}
                transparent={fillIsTransparent}
                depthWrite={!fillIsTransparent}
              />
            ) : (
              <meshStandardMaterial
                color={materialColor}
                roughness={0.78}
                side={THREE.DoubleSide}
                opacity={fillOpacity}
                transparent={fillIsTransparent}
                depthWrite={!fillIsTransparent}
              />
            )}
            <SelectedMeshOutline visible={selected} />
          </mesh>
        </group>
        {rotationControlsVisible &&
          rotationObject &&
          createPortal(
            <TransformControls
              ref={transformControlsRef}
              object={rotationObject}
              mode="rotate"
              space="local"
              rotationSnap={
                editorStore.isShiftPressed ? ROTATION_GIZMO_SNAP_STEP : null
              }
              size={rotationControlSize}
              onMouseDown={beginRotation}
              onObjectChange={updateRotation}
              onMouseUp={finishRotation}
            />,
            scene,
          )}
      </>
    );
  },
);

function decomposeMatrix(matrix: THREE.Matrix4): {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
} {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix.decompose(position, quaternion, scale);
  return { position, quaternion, scale };
}

function applyMatrixTransform(object: THREE.Object3D, matrix: THREE.Matrix4) {
  const transform = decomposeMatrix(matrix);
  object.position.copy(transform.position);
  object.quaternion.copy(transform.quaternion);
  object.scale.copy(transform.scale);
}

function getShapePrimitiveLocalRotationFromObject(
  object: THREE.Object3D,
  symbol: SymbolType,
  walls: readonly WallType[],
) {
  const baseMatrix = createShapePrimitiveWorldMatrix(
    {
      ...symbol,
      threeD: {
        ...symbol.threeD,
        rotation3D: undefined,
      },
    },
    walls,
  );
  const baseQuaternion = decomposeMatrix(baseMatrix).quaternion;
  const localQuaternion = baseQuaternion
    .clone()
    .invert()
    .multiply(object.quaternion);
  return getSymbol3DRotationFromQuaternion(localQuaternion);
}

export default ShapePrimitive3DMeshes;
